import { X } from 'lucide-react';
import { useRef, useState } from 'react';

import { MeasurementRow } from './measurement-row';
import { Pill } from './pill';
import type { DiameterGroup as DiameterGroupData } from '../../hooks/use-sizing-state';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export interface DiameterGroupProps {
    group: DiameterGroupData;
    selectedMeasurementId: string | null;
    onSelect: (id: string) => void;
    onAddOffset: (keypointId: string, offsetMm: number) => void;
    onRemoveOffset?: (offsetId: string) => void;
}

export interface DiameterOffsetPillProps {
    id: string;
    label: string;
    value: number;
    unit: string;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onDelete?: () => void;
}

export function DiameterOffsetPill({
    id,
    label,
    value,
    unit,
    isSelected,
    onSelect,
    onDelete,
}: DiameterOffsetPillProps) {
    return (
        <Pill variant="selectable" isSelected={isSelected} onClick={() => onSelect(id)}>
            <span className="tabular-nums font-mono">
                {label} <span className="text-xs">→</span> {value.toFixed(1)} {unit}
            </span>
            {onDelete && (
                <button
                    className="shrink-0 opacity-50 group-hover/pill:opacity-100 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title="Remove"
                >
                    <X className="h-3 w-3 transition-all duration-200 hover:text-destructive" />
                </button>
            )}
        </Pill>
    );
}

export function DiameterGroup({
    group,
    selectedMeasurementId,
    onSelect,
    onAddOffset,
    onRemoveOffset,
}: DiameterGroupProps) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [offsetValue, setOffsetValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSubmit = () => {
        const parsed = parseFloat(offsetValue);
        if (isNaN(parsed) || parsed === 0) return;
        onAddOffset(group.keypointId, parsed);
        setDialogOpen(false);
        setOffsetValue('');
    };

    return (
        <div>
            <MeasurementRow
                id={group.main.id}
                name={group.main.shortLabel}
                value={group.main.value}
                unit={group.main.unit}
                isSelected={selectedMeasurementId === group.main.id}
                onSelect={onSelect}
            />
            <div className="flex flex-wrap items-center gap-1 py-1 pl-4 pr-2">
                {[...group.offsets]
                    .sort((a, b) => {
                        if (a.context.type === 'offset-diameter' && b.context.type === 'offset-diameter') {
                            return a.context.offsetMm - b.context.offsetMm;
                        }
                        return 0;
                    })
                    .map((offset) => (
                        <DiameterOffsetPill
                            key={offset.id}
                            id={offset.id}
                            label={
                                offset.context.type === 'offset-diameter'
                                    ? `${offset.context.offsetMm >= 0 ? '+' : ''}${offset.context.offsetMm}`
                                    : offset.name
                            }
                            value={offset.value}
                            unit={offset.unit}
                            isSelected={selectedMeasurementId === offset.id}
                            onSelect={onSelect}
                            onDelete={
                                offset.context.type === 'offset-diameter' && !isDefaultOffset(offset.id)
                                    ? () => onRemoveOffset?.(offset.id)
                                    : undefined
                            }
                        />
                    ))}
                <Pill variant="add" onClick={() => setDialogOpen(true)}>
                    + offset
                </Pill>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Add offset diameter from {group.keypointId}</DialogTitle>
                        <DialogDescription>
                            Enter the distance in mm from {group.keypointId}. Positive values go distal
                            (toward bifurcation), negative values go proximal.
                        </DialogDescription>
                    </DialogHeader>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <Input
                                ref={inputRef}
                                type="number"
                                step="any"
                                placeholder="e.g. 10 or -5"
                                value={offsetValue}
                                onChange={(e) => setOffsetValue(e.target.value)}
                                autoFocus
                            />
                            <span className="shrink-0 text-sm text-muted-foreground">mm</span>
                        </div>
                        <DialogFooter className="mt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={!offsetValue || parseFloat(offsetValue) === 0}>
                                Add
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function isDefaultOffset(id: string): boolean {
    // Custom offsets use "custom-offset-" prefix; template offsets use IDs like "dP2+10"
    return !id.startsWith('custom-offset-');
}
